import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import AppHeader from "@/components/AppHeader";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/contexts/AuthContext";
import { authFetch } from "@/utils/authFetch";
import { DatePickerInput } from "@/components/DatePickerInput";
import {
  FileText, Download, Clock, CheckCircle2, CreditCard,
  AlertCircle, MapPin, Wrench, ChevronRight, Plus, Search,
  Loader2, XCircle, Eye, Award, Copy, Leaf, Flame, ClipboardCheck, Zap, Pencil, RotateCcw, MessageSquare, Share2
} from "lucide-react";
import { toast } from "sonner";
import ShareModal from "@/components/ShareModal";
import QuickSmsModal from "@/components/QuickSmsModal";
import CreditShopModal from "@/components/CreditShopModal";
import { getPendingSync, processSyncQueue } from "@/utils/syncQueue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: "Draft", icon: Clock, color: "bg-[#F3F4F6] text-[#71717A] border-[#E4E4E7]" },
  pending_payment: { label: "Pending Payment", icon: CreditCard, color: "bg-yellow-50 text-[#92400E] border-[#EAB308]" },
  paid: { label: "Paid", icon: CheckCircle2, color: "bg-blue-50 text-[#D4A537] border-[#D4A537]" },
  completed: { label: "Completed", icon: CheckCircle2, color: "bg-green-50 text-[#166534] border-[#D4A537]" },
};

const CERT_TYPE_LABELS = {
  domestic_eic: "Domestic EIC",
  minor_works: "Minor Works",
  three_circuit_mw: "3 Circuit MW",
  mcs_solar_pv_install: "Solar PV Install",
  mcs_solar_pv_commissioning: "Solar PV Commission",
  mcs_solar_pv_array_test: "PV Array Test",
  mcs_solar_pv_handover: "PV Handover",
  mcs_solar_pv_battery_handover: "PV & Battery Handover",
  mcs_battery_handover: "Battery Handover",
  mcs_battery_commissioning: "Battery Commission",
  mcs_battery_testing: "Battery Testing",
  mcs_heat_pump_commissioning: "HP Commission",
  mcs_heat_pump_handover: "HP Handover",
  mcs_cylinder_commissioning: "Cylinder Commission",
  mcs_dno_ev_hp: "DNO EV/HP App",
  earthing_bonding: "Earthing & Bonding",
  danger_notice: "Danger Notice",
  visual_inspection: "Visual Inspection",
  ev_charger: "EV Charger",
  ventilation: "Ventilation",
  electrical_risk: "Risk Assessment",
  electrical_isolation: "Isolation Cert",
  landlords_interim: "Landlords Interim",
  ev_risk_assessment: "EV Risk Assessment",
  eicr: "EICR",
  eic: "EIC",
  solar_pv_elec: "Solar PV",
  pat_testing: "PAT Testing",
};

export default function DashboardPage() {
  const [notifications, setNotifications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("notifications");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedCerts, setSelectedCerts] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareCertId, setShareCertId] = useState(null);
  const [quickSmsCert, setQuickSmsCert] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [serverSearchResults, setServerSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [pendingSync, setPendingSync] = useState([]);
  const [credits, setCredits] = useState({ electrical_credits: 0, mcs_credits: 0, pat_credits: 0, gas_credits: 0 });
  const [smsCredits, setSmsCredits] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [upcomingServices, setUpcomingServices] = useState([]);
  const navigate = useNavigate();

  // Check pending offline sync queue
  useEffect(() => {
    getPendingSync().then(setPendingSync).catch(() => {});
  }, []);

  const handleManualSync = async () => {
    const { synced, failed } = await processSyncQueue();
    if (synced > 0) toast.success(`${synced} certificate${synced > 1 ? "s" : ""} synced!`);
    if (failed > 0) toast.error(`${failed} failed to sync`);
    getPendingSync().then(setPendingSync).catch(() => {});
  };

  // Server-side search when 3+ chars typed
  useEffect(() => {
    if (searchTerm.length < 3) { setServerSearchResults(null); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/certificates/search?q=${encodeURIComponent(searchTerm)}`, { withCredentials: true });
        setServerSearchResults(data.results || []);
      } catch { setServerSearchResults(null); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const buySmsCredits = async () => {
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/sms/purchase`, { origin_url: origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error("Failed to start SMS purchase. Please try again.");
    }
  };

  useEffect(() => {
    // Small delay to ensure auth cookie is set after login redirect
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      const [notifRes, certRes, creditsRes] = await Promise.all([
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/certificates`, { withCredentials: true }),
        axios.get(`${API}/credits/balance`, { withCredentials: true }),
      ]);
      setNotifications(notifRes.data.notifications || []);
      setCertificates(certRes.data.certificates || []);
      setCredits(creditsRes.data || { electrical_credits: 0, mcs_credits: 0, pat_credits: 0, gas_credits: 0 });
      try {
        const smsRes = await axios.get(`${API}/sms/balance`, { withCredentials: true });
        setSmsCredits(smsRes.data?.sms_credits || 0);
      } catch { setSmsCredits(0); }
      try {
        const remRes = await axios.get(`${API}/reminders/upcoming`, { withCredentials: true });
        setUpcomingServices(Array.isArray(remRes.data) ? remRes.data : []);
      } catch { setUpcomingServices([]); }
    } catch (err) {
      // Don't show error toast for auth issues (user just logged in)
      if (err?.response?.status !== 401) {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const buyCredits = async (type) => {
    try {
      const res = await authFetch(`${API}/credits/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credit_type: type, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      toast.error("Failed to initiate purchase");
    }
  };

  const electricalCerts = certificates.filter((c) => !(c.type || "").startsWith("mcs_") && (c.type || "") !== "pat_testing" && !(c.type || "").startsWith("gas_") && !(c.type || "").startsWith("gas-"));
  const mcsCerts = certificates.filter((c) => (c.type || "").startsWith("mcs_"));
  const gasCerts = certificates.filter((c) => (c.type || "").startsWith("gas_") || (c.type || "").startsWith("gas-") || (c.type || "") === "gas_boiler_commissioning");
  const patCerts = certificates.filter((c) => (c.type || "") === "pat_testing");

  const downloadCertificate = (notificationId) => {
    window.open(`${API}/certificates/${notificationId}/download`, "_blank");
  };

  const downloadEICCert = (certId) => {
    window.open(`${API}/certificates/domestic-eic/${certId}/download`, "_blank");
  };

  const toggleCertSelect = (certId) => {
    setSelectedCerts((prev) => {
      const next = new Set(prev);
      if (next.has(certId)) next.delete(certId); else next.add(certId);
      return next;
    });
  };

  const bulkExport = async () => {
    if (selectedCerts.size === 0) { toast.error("Select certificates to export"); return; }
    setExporting(true);
    try {
      const res = await authFetch(`${API}/certificates/bulk-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ certificate_ids: Array.from(selectedCerts) }),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "certificates_export.zip"; a.click();
      URL.revokeObjectURL(url);
      setSelectedCerts(new Set());
      toast.success(`Exported ${selectedCerts.size} certificates`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const duplicateCertificate = (cert) => {
    const route = getCertRoute(cert.type);
    if (route) {
      sessionStorage.setItem("duplicate_cert_data", JSON.stringify(cert));
      window.location.href = route;
    } else {
      toast.error("Cannot duplicate this certificate type");
    }
  };

  const editCertificate = async (cert) => {
    const route = getCertRoute(cert.type);
    if (!route) { toast.error("Cannot edit this certificate type"); return; }
    try {
      const { data } = await axios.get(`${API}/certificates/${cert.certificate_id}/data`, { withCredentials: true });
      sessionStorage.setItem("edit_cert_data", JSON.stringify({ ...data, _edit_id: cert.certificate_id }));
      window.location.href = route;
    } catch {
      toast.error("Failed to load certificate data");
    }
  };

  const getCertRoute = (certType) => {
    const t = certType || "";
    const typeRoutes = {
      domestic_eic: "/certificates/domestic-eic",
      minor_works: "/certificates/minor-works",
      three_circuit_mw: "/certificates/3-circuit-minor",
      pat_testing: "/certificates/pat-testing",
      gas_boiler_commissioning: "/certificates/gas-safe/boiler-commissioning",
    };
    let route = typeRoutes[t];
    if (!route && (t.startsWith("mcs_") || t.startsWith("mcs-"))) {
      const slug = t.replace(/^mcs[_-]/, "").replace(/_/g, "-");
      route = `/certificates/mcs/${slug}`;
    }
    if (!route && (t.startsWith("gas_") || t.startsWith("gas-"))) {
      const slug = t.replace(/_/g, "-");
      route = `/certificates/gas-safe/${slug}`;
    }
    if (!route) {
      const slug = t.replace(/_/g, "-");
      route = `/certificates/elec/${slug}`;
    }
    return route;
  };

  const retryPayment = async (notificationId) => {
    try {
      const origin = window.location.origin;
      const { data: checkout } = await axios.post(
        `${API}/payments/create-checkout`,
        { notification_id: notificationId, origin_url: origin },
        { withCredentials: true }
      );
      if (checkout.checkout_url) {
        window.location.href = checkout.checkout_url;
      }
    } catch {
      toast.error("Failed to create checkout session");
    }
  };

  const repeatNotification = (n) => {
    const prefill = {
      premise: n.premise || "", street: n.street || "", locality: n.locality || "",
      town: n.town || "", county: n.county || "", postcode: n.postcode || "",
      country: n.country || "", region: n.region || "",
      local_authority: n.local_authority || "", local_authority_email: n.local_authority_email || "",
      homeowner_name: n.homeowner_name || "", homeowner_email: n.homeowner_email || "",
      homeowner_phone: n.homeowner_phone || "",
      technician_name: n.technician_name || "", technician_company: n.technician_company || "",
      technician_email: n.technician_email || "",
    };
    sessionStorage.setItem("repeat_notification_data", JSON.stringify(prefill));
    navigate("/notification");
    toast.success("Address and customer details pre-filled");
  };

  const repeatCertificate = (cert) => {
    const route = getCertRoute(cert.type);
    if (!route) { toast.error("Cannot repeat this certificate type"); return; }
    const prefill = {
      client_name: cert.client_name || "", installation_address: cert.installation_address || "",
      installation_postcode: cert.installation_postcode || "",
      contractor_trading_title: cert.contractor_trading_title || "",
      contractor_email: cert.contractor_email || "", contractor_phone: cert.contractor_phone || "",
    };
    sessionStorage.setItem("repeat_cert_data", JSON.stringify(prefill));
    navigate(route);
    toast.success("Customer and address details pre-filled");
  };

  const shareCertificate = async (certId, email, expiryDays) => {
    try {
      const { data } = await axios.post(`${API}/certificates/${certId}/share`,
        { customer_email: email || "", expires_in_days: expiryDays }, { withCredentials: true });
      if (data.url) {
        try { await navigator.clipboard.writeText(data.url); } catch { /* clipboard unavailable */ }
        if (!email) toast.success("Share link copied to clipboard!");
      }
      return data;
    } catch { toast.error("Failed to create share link"); return null; }
  };

  const openShareModal = (certId) => {
    setShareCertId(certId);
    setShareModalOpen(true);
  };



  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const matchesDateRange = (dateStr) => {
    if (!dateFrom && !dateTo) return true;
    if (!dateStr) return true;
    const d = dateStr.slice(0, 10); // "YYYY-MM-DD" or "DD/MM/YYYY"
    // Normalize to comparable format
    const norm = d.includes("/") ? d.split("/").reverse().join("-") : d;
    if (dateFrom && norm < dateFrom) return false;
    if (dateTo && norm > dateTo) return false;
    return true;
  };

  const filtered = notifications.filter((n) => {
    if (!matchesDateRange(n.installation_date || n.created_at)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (n.certificate_ref || "").toLowerCase().includes(term) ||
      (n.postcode || "").toLowerCase().includes(term) ||
      (n.homeowner_name || "").toLowerCase().includes(term) ||
      (n.local_authority || "").toLowerCase().includes(term) ||
      (n.town || "").toLowerCase().includes(term) ||
      (n.street || "").toLowerCase().includes(term)
    );
  });

  const filteredCerts = certificates.filter((cert) => {
    const t = cert.type || "";
    // Tab-level filter
    if (activeTab === "electrical" && (t.startsWith("mcs_") || t === "pat_testing" || t.startsWith("gas_"))) return false;
    if (activeTab === "mcs" && !t.startsWith("mcs_")) return false;
    if (activeTab === "gas_safe" && !t.startsWith("gas_") && !t.startsWith("gas-") && t !== "gas_boiler_commissioning") return false;
    if (activeTab === "pat" && t !== "pat_testing") return false;
    // Type dropdown filter
    if (typeFilter !== "all") {
      if (typeFilter === "electrical" && (t.startsWith("mcs_") || t === "pat_testing" || t.startsWith("gas_"))) return false;
      if (typeFilter === "mcs" && !t.startsWith("mcs_")) return false;
      if (typeFilter !== "electrical" && typeFilter !== "mcs" && cert.type !== typeFilter) return false;
    }
    // Date range
    if (!matchesDateRange(cert.created_at)) return false;
    // Search
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (cert.certificate_id || "").toLowerCase().includes(term) ||
      (cert.installation_address || "").toLowerCase().includes(term) ||
      (cert.client_name || "").toLowerCase().includes(term) ||
      (cert.contractor_trading_title || "").toLowerCase().includes(term) ||
      (cert.type || "").toLowerCase().includes(term)
    );
  });

  const { user: authUser } = useAuth();
  const firstName = (authUser?.name || "").split(" ")[0] || "there";
  const companyName = authUser?.company || "";

  return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="dashboard-page">
      <AppHeader />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Greeting */}
        <div className="mb-6" data-testid="dashboard-greeting">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }} data-testid="dashboard-title">
            Hi {firstName} <span role="img" aria-label="wave">&#x1F44B;</span>
          </h1>
          {companyName && <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{companyName}</p>}
        </div>

        {/* Pending Sync Banner */}
        {pendingSync.length > 0 && (
          <div className="flex items-center gap-3 rounded-2xl p-3 mb-4" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }} data-testid="sync-banner">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-100">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold" style={{ color: '#1E40AF' }}>{pendingSync.length} certificate{pendingSync.length > 1 ? "s" : ""} waiting to sync</p>
              <p className="text-[10px]" style={{ color: '#3B82F6' }}>Saved offline — {navigator.onLine ? "tap to sync now" : "will auto-sync when online"}</p>
            </div>
            {navigator.onLine && (
              <button onClick={handleManualSync} className="px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-testid="sync-now-btn">
                Sync Now
              </button>
            )}
          </div>
        )}

        {/* Credits Row */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Your credits</h2>
            <button onClick={() => setShopOpen(true)} className="text-sm font-semibold text-[#00A3E0] hover:underline" data-testid="buy-credits-link">Buy credits</button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="credit-balance">
            {[
              { label: "Electrical", credits: credits.electrical_credits, color: "#00A3E0", bg: "#E8F7FC", type: "electrical", Icon: Zap, pack: "20 for £5" },
              { label: "Gas &\nBoiler", credits: credits.gas_credits, color: "#EF4444", bg: "#FEECEC", type: "gas", Icon: Flame, pack: "50 for £5" },
              { label: "MCS /\nRenewables", credits: credits.mcs_credits, color: "#22C55E", bg: "#EAFBF0", type: "mcs", Icon: Leaf, pack: "20 for £5" },
              { label: "PAT\nTesting", credits: credits.pat_credits, color: "#8B5CF6", bg: "#F3EFFE", type: "pat", Icon: ClipboardCheck, pack: "10 for £5" },
            ].map((c) => (
              <button key={c.type} onClick={() => buyCredits(c.type)} className="rounded-2xl p-3 text-left transition-all hover:shadow-md" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`credit-card-${c.type}`}>
                <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{Math.max(0, c.credits)} <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>left</span></p>
                <p className="text-[9px] font-semibold mb-2 px-1.5 py-0.5 rounded-full inline-block" style={{ backgroundColor: c.bg, color: c.color }}>{c.pack}</p>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-1" style={{ backgroundColor: c.bg }}>
                  <c.Icon className="w-5 h-5" style={{ color: c.color }} />
                </div>
                <p className="text-[11px] leading-tight whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>{c.label}</p>
              </button>
            ))}
            {/* SMS Credits */}
            <button onClick={buySmsCredits} className="rounded-2xl p-3 text-left transition-all hover:shadow-md" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid="credit-card-sms">
              <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{smsCredits} <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>left</span></p>
              <p className="text-[9px] font-semibold mb-2 px-1.5 py-0.5 rounded-full inline-block" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>100 for £7</p>
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-1" style={{ backgroundColor: '#FEF3C7' }}>
                <FileText className="w-5 h-5 text-[#D97706]" />
              </div>
              <p className="text-[11px] leading-tight" style={{ color: 'var(--text-secondary)' }}>SMS</p>
            </button>
          </div>
        </div>

        {/* Building Control */}
        <div className="mb-6">
          <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Building Control</h2>
          <Link to="/notification" data-testid="new-notification-btn"
            className="flex items-center gap-4 rounded-2xl p-4 transition-all hover:shadow-md group"
            style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8F4FD' }}>
              <FileText className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Building Notifications</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Notify Part P work · £4.99 each</p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0 group-hover:text-gray-500 transition-colors" style={{ color: 'var(--text-muted)' }} />
          </Link>
        </div>

        {/* Create a document */}
        <div className="mb-6">
          <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Create a document</h2>
          <div className="space-y-3" data-testid="action-buttons">
            {[
              { to: "/new/electrical", testId: "new-certificate-btn", color: "#00A3E0", bg: "#E8F7FC", Icon: Zap, label: "Electrical", sub: "13 document types" },
              { to: "/new/gas-safe", testId: "gas-safe-btn", color: "#EF4444", bg: "#FEECEC", Icon: Flame, label: "Gas and Heating Documents", sub: "8 document types" },
              { to: "/new/mcs", testId: "new-mcs-btn", color: "#22C55E", bg: "#EAFBF0", Icon: Leaf, label: "MCS / Renewables", sub: "12 document types" },
              { to: "/certificates/pat-testing", testId: "pat-testing-btn", color: "#8B5CF6", bg: "#F3EFFE", Icon: ClipboardCheck, label: "PAT Testing", sub: "1 document type" },
            ].map((btn) => (
              <Link key={btn.testId} to={btn.to} data-testid={btn.testId}
                className="flex items-center gap-4 rounded-2xl p-4 transition-all hover:shadow-md group"
                style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: btn.bg }}>
                  <btn.Icon className="w-6 h-6" style={{ color: btn.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>{btn.label}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{btn.sub}</p>
                </div>
                <ChevronRight className="w-5 h-5 flex-shrink-0 group-hover:text-gray-500 transition-colors" style={{ color: 'var(--text-muted)' }} />
              </Link>
            ))}
          </div>
        </div>

        {/* SMS */}
        <div className="mb-6">
          <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>SMS</h2>
          <Link to="/sms" data-testid="sms-centre-btn"
            className="flex items-center gap-4 rounded-2xl p-4 transition-all hover:shadow-md group"
            style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3C7' }}>
              <MessageSquare className="w-6 h-6 text-[#D97706]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>SMS Centre</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Send reminders to customers</p>
            </div>
            <ChevronRight className="w-5 h-5 flex-shrink-0 group-hover:text-gray-500 transition-colors" style={{ color: 'var(--text-muted)' }} />
          </Link>
        </div>

        {/* Upcoming Service Reminders */}
        {upcomingServices.length > 0 && (
          <div className="bg-white rounded-2xl p-4 mb-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid="service-reminders">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-[#EF4444]" />
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Reminders</h3>
              <span className="text-[10px] font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{upcomingServices.length}</span>
            </div>
            <div className="space-y-2">
              {upcomingServices.slice(0, 5).map((s) => (
                <div key={s.certificate_id} className="flex items-center justify-between gap-3 p-2.5 rounded-xl text-xs" style={{ backgroundColor: s.is_overdue ? '#FEF2F2' : '#FFF7ED' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertCircle className={`w-3.5 h-3.5 flex-shrink-0 ${s.is_overdue ? 'text-red-600' : 'text-amber-600'}`} />
                    <span className="font-bold truncate">{s.customer_name || 'Customer'}</span>
                    <span className="text-[#8E8E93] truncate hidden sm:inline">{s.service_address}</span>
                  </div>
                  <div className="flex-shrink-0">
                    {s.is_overdue ? (
                      <span className="text-[10px] font-bold text-red-700 bg-red-200 px-2 py-0.5 rounded-full">OVERDUE</span>
                    ) : (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full">Due {s.next_service_due}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History Tabs */}
        <div className="flex gap-0 mb-4 overflow-x-auto border-b" style={{ borderColor: 'var(--border-main)' }} data-testid="dashboard-tabs">
          {[
            { id: "all", label: "All", count: notifications.length + certificates.length, icon: Search, color: "#1C1C1E" },
            { id: "notifications", label: "Notifications", count: notifications.length, icon: FileText, color: "#D4A537" },
            { id: "electrical", label: "Electrical", count: electricalCerts.length, icon: Zap, color: "#00A3E0" },
            { id: "mcs", label: "MCS", count: mcsCerts.length, icon: Leaf, color: "#22C55E" },
            { id: "gas_safe", label: "Gas Safe", count: gasCerts.length, icon: Flame, color: "#EF4444" },
            { id: "pat", label: "PAT", count: patCerts.length, icon: ClipboardCheck, color: "#8B5CF6" },
          ].map((tab) => (
            <button key={tab.id} data-testid={`tab-${tab.id}`}
              onClick={() => { setActiveTab(tab.id); setSelectedCerts(new Set()); }}
              className="px-3 py-2.5 text-xs font-semibold transition-all -mb-[1px] border-b-2 whitespace-nowrap"
              style={{
                fontFamily: 'Outfit, sans-serif',
                borderColor: activeTab === tab.id ? tab.color : 'transparent',
                color: activeTab === tab.id ? tab.color : 'var(--text-secondary)',
              }}
            >
              <tab.icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="dashboard-filters">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
            <input
              data-testid="dashboard-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border px-3 pl-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-transparent"
              placeholder={activeTab === "all" ? "Search all certificates and notifications..." : activeTab === "notifications" ? "Search notifications..." : "Search certificates..."}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif', backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>From</label>
            <DatePickerInput testId="filter-date-from" value={dateFrom} onChange={setDateFrom} placeholder="From" />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>To</label>
            <DatePickerInput testId="filter-date-to" value={dateTo} onChange={setDateTo} placeholder="To" />
          </div>
          {activeTab !== "notifications" && (
            <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-white rounded-xl border border-[#E5E5EA] px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#00A3E0] w-[160px]">
              <option value="all">All Types</option>
              {Object.entries(CERT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          )}
          {(dateFrom || dateTo || typeFilter !== "all") && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); setTypeFilter("all"); }} data-testid="clear-filters-btn"
              className="text-xs font-semibold text-[#00A3E0] hover:underline whitespace-nowrap self-center">Clear</button>
          )}
          {(activeTab === "electrical" || activeTab === "mcs") && selectedCerts.size > 0 && (
            <button data-testid="bulk-export-btn" onClick={bulkExport} disabled={exporting}
              className="bg-[#00A3E0] text-white rounded-xl px-4 py-2 flex items-center gap-2 text-xs font-semibold whitespace-nowrap hover:bg-[#0082B3] transition-colors">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Export {selectedCerts.size}
            </button>
          )}
        </div>

        {/* Server Search Results */}
        {searchTerm.length >= 3 && serverSearchResults && serverSearchResults.length > 0 && (
          <div className="mb-4 rounded-2xl p-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid="server-search-results">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-[#00A3E0]" />
              <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
                {serverSearchResults.length} result{serverSearchResults.length !== 1 ? "s" : ""} for "{searchTerm}"
              </p>
              {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00A3E0]" />}
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              {serverSearchResults.slice(0, 10).map((cert) => (
                <div key={cert.certificate_id} className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:opacity-90" style={{ backgroundColor: 'var(--row-alt)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E8F7FC' }}>
                    <FileText className="w-4 h-4 text-[#00A3E0]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{cert.certificate_id}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{cert.client_name || cert.client_company || "—"} · {cert.installation_address || cert.client_address || ""}</p>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#E8F7FC', color: '#00A3E0' }}>
                    {(cert.type || "").replace(/_/g, " ")}
                  </span>
                </div>
              ))}
              {serverSearchResults.length > 10 && (
                <p className="text-[10px] text-center py-1" style={{ color: 'var(--text-muted)' }}>Showing 10 of {serverSearchResults.length} results</p>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="dashboard-loading">
            <Loader2 className="w-8 h-8 text-[#D4A537] animate-spin" />
          </div>
        ) : activeTab === "all" ? (
          (() => {
            const allCerts = certificates.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase()));
            const allNotifs = filtered;
            const total = allCerts.length + allNotifs.length;
            if (total === 0) return <EmptyState icon={Search} label={searchTerm ? "No matching results" : "No documents yet"} searchTerm={searchTerm} />;
            return (
              <div className="space-y-3" data-testid="all-list">
                {allNotifs.map((n) => (
                  <NotificationCard key={n.id} notification={n} expanded={expandedId === n.id}
                    onToggle={() => toggleExpand(n.id)} onDownload={() => downloadCertificate(n.id)} onRetryPayment={() => retryPayment(n.id)} onRepeat={() => repeatNotification(n)} onShare={() => openShareModal(n.id)} onSms={() => setQuickSmsCert({ certificate_id: n.id, client_name: n.homeowner_name, installation_address: [n.premise, n.street, n.town].filter(Boolean).join(", "), homeowner_name: n.homeowner_name })} />
                ))}
                {allCerts.map((cert) => (
                  <CertificateCard key={cert.certificate_id} cert={cert} selected={selectedCerts.has(cert.certificate_id)}
                    onToggleSelect={() => toggleCertSelect(cert.certificate_id)} onDownload={() => downloadEICCert(cert.certificate_id)} onDuplicate={() => duplicateCertificate(cert)} onEdit={() => editCertificate(cert)} onRepeat={() => repeatCertificate(cert)} onShare={() => openShareModal(cert.certificate_id)} onSms={() => setQuickSmsCert(cert)} />
                ))}
              </div>
            );
          })()
        ) : activeTab === "notifications" ? (
          filtered.length === 0 ? (
            <EmptyState icon={FileText} label={searchTerm ? "No matching notifications" : "No notifications yet"} link="/notification" btnLabel="Create Notification" searchTerm={searchTerm} />
          ) : (
            <div className="space-y-3" data-testid="notifications-list">
              {filtered.map((n) => (
                <NotificationCard key={n.id} notification={n} expanded={expandedId === n.id}
                  onToggle={() => toggleExpand(n.id)} onDownload={() => downloadCertificate(n.id)} onRetryPayment={() => retryPayment(n.id)} onRepeat={() => repeatNotification(n)} onShare={() => openShareModal(n.id)} onSms={() => setQuickSmsCert({ certificate_id: n.id, client_name: n.homeowner_name, installation_address: [n.premise, n.street, n.town].filter(Boolean).join(", "), homeowner_name: n.homeowner_name })} />
              ))}
            </div>
          )
        ) : activeTab === "electrical" ? (
          electricalCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
            <EmptyState icon={Zap} label={searchTerm ? "No matching certificates" : "No electrical certificates yet"} link="/new/electrical" btnLabel="Create Certificate" searchTerm={searchTerm} />
          ) : (
            <div className="space-y-3" data-testid="electrical-list">
              {electricalCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).map((cert) => (
                <CertificateCard key={cert.certificate_id} cert={cert} selected={selectedCerts.has(cert.certificate_id)}
                  onToggleSelect={() => toggleCertSelect(cert.certificate_id)} onDownload={() => downloadEICCert(cert.certificate_id)} onDuplicate={() => duplicateCertificate(cert)} onEdit={() => editCertificate(cert)} onRepeat={() => repeatCertificate(cert)} onShare={() => openShareModal(cert.certificate_id)} onSms={() => setQuickSmsCert(cert)} />
              ))}
            </div>
          )
        ) : activeTab === "mcs" ? (
          mcsCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
            <EmptyState icon={Leaf} label={searchTerm ? "No matching MCS documents" : "No MCS documents yet"} link="/new/mcs" btnLabel="Create MCS Document" searchTerm={searchTerm} />
          ) : (
            <div className="space-y-3" data-testid="mcs-list">
              {mcsCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).map((cert) => (
                <CertificateCard key={cert.certificate_id} cert={cert} selected={selectedCerts.has(cert.certificate_id)}
                  onToggleSelect={() => toggleCertSelect(cert.certificate_id)} onDownload={() => downloadEICCert(cert.certificate_id)} onDuplicate={() => duplicateCertificate(cert)} onEdit={() => editCertificate(cert)} onRepeat={() => repeatCertificate(cert)} onShare={() => openShareModal(cert.certificate_id)} onSms={() => setQuickSmsCert(cert)} />
              ))}
            </div>
          )
        ) : activeTab === "gas_safe" ? (
          gasCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
            <EmptyState icon={Flame} label={searchTerm ? "No matching Gas Safe documents" : "No Gas Safe documents yet"} link="/new/gas-safe" btnLabel="Create Gas Safe Document" searchTerm={searchTerm} />
          ) : (
            <div className="space-y-3" data-testid="gas-safe-list">
              {gasCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).map((cert) => (
                <CertificateCard key={cert.certificate_id} cert={cert} selected={selectedCerts.has(cert.certificate_id)}
                  onToggleSelect={() => toggleCertSelect(cert.certificate_id)} onDownload={() => downloadEICCert(cert.certificate_id)} onDuplicate={() => duplicateCertificate(cert)} onEdit={() => editCertificate(cert)} onRepeat={() => repeatCertificate(cert)} onShare={() => openShareModal(cert.certificate_id)} onSms={() => setQuickSmsCert(cert)} />
              ))}
            </div>
          )
        ) : activeTab === "pat" ? (
          patCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).length === 0 ? (
            <EmptyState icon={ClipboardCheck} label={searchTerm ? "No matching PAT reports" : "No PAT testing reports yet"} link="/certificates/pat-testing" btnLabel="Create PAT Report" searchTerm={searchTerm} />
          ) : (
            <div className="space-y-3" data-testid="pat-list">
              {patCerts.filter((c) => !searchTerm || JSON.stringify(c).toLowerCase().includes(searchTerm.toLowerCase())).map((cert) => (
                <CertificateCard key={cert.certificate_id} cert={cert} selected={selectedCerts.has(cert.certificate_id)}
                  onToggleSelect={() => toggleCertSelect(cert.certificate_id)} onDownload={() => downloadEICCert(cert.certificate_id)} onDuplicate={() => duplicateCertificate(cert)} onEdit={() => editCertificate(cert)} onRepeat={() => repeatCertificate(cert)} onShare={() => openShareModal(cert.certificate_id)} onSms={() => setQuickSmsCert(cert)} />
              ))}
            </div>
          )
        ) : null}
      </div>
      <BottomNav />
      <ShareModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} certId={shareCertId} onShare={shareCertificate} />
      <QuickSmsModal open={!!quickSmsCert} onClose={() => setQuickSmsCert(null)} certData={quickSmsCert} />
      <CreditShopModal open={shopOpen} onClose={() => setShopOpen(false)} onBuy={buyCredits} onBuySms={buySmsCredits} />
    </div>
  );
}

function EmptyState({ icon: Icon, label, link, btnLabel, searchTerm }) {
  return (
    <div className="rounded-2xl text-center py-12 px-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
      <Icon className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>{label}</h3>
      <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
        {searchTerm ? "Try a different search term." : "Get started by creating one."}
      </p>
      {!searchTerm && link && <Link to={link} className="inline-flex items-center gap-2 bg-[#00A3E0] text-white rounded-xl px-5 py-2.5 font-semibold text-sm hover:bg-[#0082B3] transition-colors"><Plus className="w-4 h-4" /> {btnLabel}</Link>}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-[#0A0A0A]" }) {
  return (
    <div className="brutalist-card p-4" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#71717A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {label}
        </span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{value}</p>
    </div>
  );
}

function CertificateCard({ cert, onDownload, onDuplicate, onEdit, onRepeat, onShare, onSms, selected, onToggleSelect }) {
  const typeLabel = CERT_TYPE_LABELS[cert.type] || cert.type || "Certificate";
  const t = cert.type || "";
  const badgeColor = t.startsWith("mcs_") ? "#22C55E" : t.startsWith("gas_") || t === "gas_boiler_commissioning" ? "#EF4444" : t === "pat_testing" ? "#8B5CF6" : "#00A3E0";
  const bgTint = t.startsWith("mcs_") ? "#EAFBF0" : t.startsWith("gas_") || t === "gas_boiler_commissioning" ? "#FEECEC" : t === "pat_testing" ? "#F3EFFE" : "#E8F7FC";
  const CardIcon = t.startsWith("mcs_") ? Leaf : t.startsWith("gas_") || t === "gas_boiler_commissioning" ? Flame : t === "pat_testing" ? ClipboardCheck : Zap;
  return (
    <div className={`rounded-2xl p-4 sm:p-5 transition-all ${selected ? 'ring-2' : ''}`}
      style={{ backgroundColor: 'var(--bg-card)', boxShadow: selected ? `0 0 0 2px ${badgeColor}` : '0 1px 3px var(--shadow-color)' }}
      data-testid={`cert-card-${cert.certificate_id}`}>
      <div className="flex items-center gap-3">
        <input type="checkbox" checked={selected} onChange={onToggleSelect}
          className="w-4 h-4 flex-shrink-0 cursor-pointer rounded" style={{ accentColor: badgeColor }} data-testid={`select-cert-${cert.certificate_id}`} />
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgTint }}>
          <CardIcon className="w-5 h-5" style={{ color: badgeColor }} />
        </div>
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
          <div className="sm:col-span-2">
            <p className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>Ref</p>
            <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{cert.certificate_id}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>Type</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ backgroundColor: bgTint, color: badgeColor }}>
              {typeLabel}
            </span>
          </div>
          <div className="sm:col-span-3">
            <p className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>Address</p>
            <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{cert.installation_address || "\u2014"}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>Client</p>
            <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{cert.client_name || cert.electrician_name || "\u2014"}</p>
          </div>
          <div className="sm:col-span-3 flex justify-end gap-2">
            <button data-testid={`sms-cert-${cert.certificate_id}`} onClick={onSms}
              className="p-2 rounded-xl transition-colors" style={{ backgroundColor: '#FEF3C7' }} title="Send SMS to Customer">
              <MessageSquare className="w-3.5 h-3.5 text-[#D97706]" />
            </button>
            <button data-testid={`repeat-cert-${cert.certificate_id}`} onClick={onRepeat}
              className="p-2 rounded-xl transition-colors" style={{ backgroundColor: 'var(--row-alt)' }} title="Repeat Job">
              <RotateCcw className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
            </button>
            <button data-testid={`share-cert-${cert.certificate_id}`} onClick={onShare}
              className="p-2 rounded-xl transition-colors" style={{ backgroundColor: 'var(--row-alt)' }} title="Share with Customer">
              <Share2 className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
            </button>
            <button data-testid={`edit-cert-${cert.certificate_id}`} onClick={onEdit}
              className="p-2 rounded-xl transition-colors" style={{ backgroundColor: 'var(--row-alt)' }} title="Edit">
              <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
            </button>
            <button data-testid={`duplicate-cert-${cert.certificate_id}`} onClick={onDuplicate}
              className="p-2 rounded-xl transition-colors" style={{ backgroundColor: 'var(--row-alt)' }} title="Duplicate">
              <Copy className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
            </button>
            <button data-testid={`download-cert-${cert.certificate_id}`} onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-[#00A3E0] hover:bg-[#0082B3] transition-colors">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationCard({ notification: n, expanded, onToggle, onDownload, onRetryPayment, onRepeat, onShare, onSms }) {
  const statusConf = STATUS_CONFIG[n.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConf.icon;
  const activeWorks = (n.works || []).filter((w) => w.quantity > 0);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`notification-card-${n.id}`}>
      <button
        data-testid={`notification-toggle-${n.id}`}
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 sm:p-5 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
          <div className="sm:col-span-3">
            <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Cert Ref</p>
            <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{n.certificate_ref}</p>
          </div>
          <div className="sm:col-span-3">
            <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Address</p>
            <p className="text-sm truncate">{[n.premise, n.street, n.town].filter(Boolean).join(", ") || n.postcode || "\u2014"}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Date</p>
            <p className="text-sm">{n.installation_date || "\u2014"}</p>
          </div>
          <div className="sm:col-span-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${statusConf.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConf.label}
            </span>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <ChevronRight className={`w-5 h-5 text-gray-300 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#E5E5EA] p-4 sm:p-5 bg-gray-50/50 animate-fade-in-up" data-testid={`notification-details-${n.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <MapPin className="w-4 h-4 text-[#00A3E0]" />
                <h4 className="text-xs font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#00A3E0' }}>Address</h4>
              </div>
              <div className="space-y-1 text-sm">
                {n.premise && <p>{n.premise} {n.street}</p>}
                {!n.premise && n.street && <p>{n.street}</p>}
                {n.locality && <p>{n.locality}</p>}
                {n.town && <p>{n.town}</p>}
                {n.county && <p>{n.county}</p>}
                <p className="font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{n.postcode}</p>
              </div>
              <div className="mt-3 text-xs">
                <p className="font-semibold" style={{ color: '#8E8E93' }}>Local Authority</p>
                <p>{n.local_authority}</p>
                <p style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#8E8E93' }}>{n.local_authority_email}</p>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Eye className="w-4 h-4 text-[#00A3E0]" />
                <h4 className="text-xs font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#00A3E0' }}>Customer & Details</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-xs font-semibold" style={{ color: '#8E8E93' }}>Customer</p>
                  <p>{n.homeowner_name || "\u2014"}</p>
                  <p style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#8E8E93' }}>{n.homeowner_email}</p>
                  <p style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#8E8E93' }}>{n.homeowner_phone}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold" style={{ color: '#8E8E93' }}>Description</p>
                  <p style={{ color: '#8E8E93' }}>{n.description || "\u2014"}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Wrench className="w-4 h-4 text-[#00A3E0]" />
                <h4 className="text-xs font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#00A3E0' }}>Works</h4>
              </div>
              {activeWorks.length > 0 ? (
                <div className="space-y-1.5">
                  {activeWorks.map((w, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-[#E5E5EA] pb-1.5 last:border-0">
                      <span className="truncate pr-2">{w.name}</span>
                      <span className="font-bold flex-shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>x{w.quantity}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm" style={{ color: '#8E8E93' }}>No works recorded</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-4 border-t" style={{ borderColor: 'var(--border-main)' }}>
            {n.status === "pending_payment" && (
              <button data-testid={`retry-payment-${n.id}`} onClick={onRetryPayment}
                className="flex items-center gap-2 text-sm font-semibold bg-[#D4A537] text-white rounded-xl px-4 py-2.5 hover:bg-[#B8892C] transition-colors">
                <CreditCard className="w-4 h-4" /> Retry Payment
              </button>
            )}
            {(n.status === "completed" || n.status === "paid" || n.pdf_generated) && (
              <button data-testid={`download-cert-${n.id}`} onClick={onDownload}
                className="flex items-center gap-2 text-sm font-semibold bg-[#00A3E0] text-white rounded-xl px-4 py-2.5 hover:bg-[#0082B3] transition-colors">
                <Download className="w-4 h-4" /> Download
              </button>
            )}
            <button data-testid={`repeat-notif-${n.id}`} onClick={onRepeat}
              className="flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }}>
              <RotateCcw className="w-4 h-4" /> Repeat
            </button>
            <button data-testid={`share-notif-${n.id}`} onClick={onShare}
              className="flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }}>
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button data-testid={`sms-notif-${n.id}`} onClick={onSms}
              className="flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors" style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
              <MessageSquare className="w-4 h-4" /> SMS
            </button>
            <div className="flex-1" />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {n.created_at ? new Date(n.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "\u2014"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
